import { defineComponent, h, isReactive, onBeforeUnmount, onMounted, readonly, watch, } from 'vue';
import { vueComponentRenderer } from './vue-component-renderer.js';
const PAGE_LIFECYCLE_KEY = '__wefatPageLifecycleKey';
export class VueComponentIntegrationError extends Error {
    code = 'WF7302';
    remediation;
    constructor(message, remediation) {
        super(`WF7302 ${message}`);
        this.name = 'VueComponentIntegrationError';
        this.remediation = remediation;
    }
}
function propertyOptions(definition) {
    return Object.fromEntries(definition.propertyNames.map((name) => [name, { default: undefined }]));
}
function propertyInput(definition, props) {
    return Object.fromEntries(definition.propertyNames.map((name) => [name, props[name]]));
}
function hostAttributes(attrs) {
    return Object.fromEntries(Object.entries(attrs).filter(([name]) => !/^on[A-Z]/.test(name)));
}
function emitComponentEvent(event, emit) {
    if (event.options.bubbles || event.options.capturePhase || event.options.composed) {
        throw new VueComponentIntegrationError(`Custom event ${event.name} requests logical propagation that is not implemented`, 'Use the default non-propagating triggerEvent form until the logical component-tree dispatcher is enabled.');
    }
    emit(event.name, event.detail);
}
export function readonlyComponentData(instance) {
    if (!isReactive(instance.data)) {
        throw new VueComponentIntegrationError('Cannot expose component data created without the Vue renderer adapter', 'Create the component with vueComponentRenderer before binding its data to a generated template.');
    }
    return readonly(instance.data);
}
export function createWefatCustomComponent(definition, createRender, options = {}) {
    if (definition.kind !== 'component' || typeof createRender !== 'function') {
        throw new VueComponentIntegrationError('Custom component wrapper requires a component definition and render factory', 'Pass the outputs of createWebComponent and compileWxmlTemplate to the generated component module.');
    }
    if (options.pageLifetimes === true && definition.propertyNames.includes(PAGE_LIFECYCLE_KEY)) {
        throw new VueComponentIntegrationError(`Component property ${PAGE_LIFECYCLE_KEY} conflicts with the generated page lifecycle bridge`, `Rename the source property ${PAGE_LIFECYCLE_KEY} before using this component as a custom tab bar.`);
    }
    const bridgePageLifetimes = options.pageLifetimes === true;
    return defineComponent({
        name: `WefatCustomComponent:${definition.metadata.path}`,
        inheritAttrs: false,
        props: {
            ...propertyOptions(definition),
            ...(bridgePageLifetimes ? { [PAGE_LIFECYCLE_KEY]: { default: null } } : {}),
        },
        setup(props, { attrs, emit, slots }) {
            const dynamicProps = props;
            const instance = definition.createRenderedInstance(vueComponentRenderer, propertyInput(definition, dynamicProps), (event) => emitComponentEvent(event, emit));
            instance.attach();
            const render = createRender(instance, slots);
            const data = readonlyComponentData(instance);
            let mounted = false;
            watch(() => definition.propertyNames.map((name) => dynamicProps[name]), () => instance.updateProperties(propertyInput(definition, dynamicProps)), { deep: true, flush: 'sync' });
            if (bridgePageLifetimes) {
                watch(() => dynamicProps[PAGE_LIFECYCLE_KEY], (value, previous) => {
                    if (!mounted || Object.is(value, previous))
                        return;
                    instance.pageHide();
                    instance.pageShow();
                }, { flush: 'post' });
            }
            onMounted(() => {
                instance.ready();
                mounted = true;
                if (bridgePageLifetimes)
                    instance.pageShow();
            });
            onBeforeUnmount(() => {
                if (bridgePageLifetimes && mounted)
                    instance.pageHide();
                mounted = false;
                instance.detach();
            });
            return () => h('wefat-component', {
                ...hostAttributes(attrs),
                'data-wefat-custom-component': definition.metadata.path,
                style: { display: 'contents' },
            }, [render(data)]);
        },
    });
}
//# sourceMappingURL=custom-component.js.map